// ─── Encrypted file/image attachments — Task #150 ────────────────────────────
// See CHAT_E2E_DESIGN.md §5 (file/attachment encryption) and §6 (fileMeta
// shape). This file is the attachment-specific sibling of
// encryptedMessaging.js: same overall shape (encrypt with a fresh one-time
// key, wrap that key per-recipient, persist only ciphertext), but for file
// bytes instead of message text, plus the filename itself also gets
// encrypted (§5 point 4) rather than stored as plaintext Storage metadata.
//
// As with encryptedMessaging.js, nothing here ever sees a private key it
// wasn't handed directly by the caller, and this file never reads/writes
// IndexedDB or chatKeyBackups itself.

import { ref, uploadBytesResumable, getBytes, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase/config';
import {
  generateSymmetricKey,
  secretboxEncrypt,
  secretboxDecrypt,
  boxWrapKey,
  boxUnwrapKey,
  encryptFileBytes,
  decryptFileBytes,
} from '../../crypto/chat';
import { newChatMessageId, setChatMessage } from '../../firebase/firestore';
import { getMemberPublicKey } from './encryptedMessaging';

// Mirrors the 50MB cap in storage.rules (Task #146) — checked client-side
// too so a too-large file fails fast with a clear message instead of
// uploading most of the way and then getting rejected by the rule.
const MAX_FILE_BYTES = 50 * 1024 * 1024;

// Encrypts `file` (a browser File/Blob) with a fresh one-time key `fk`,
// uploads the ciphertext to Storage at
// companies/{companyId}/chatFiles/{threadId}/{messageId}, wraps `fk` to every
// current thread member (including the sender, same reasoning as messages —
// design doc §4 point 3), encrypts the filename with that same `fk`, and
// persists the message doc. `onProgress`, if given, is called with a 0–1
// fraction covering the encrypt + upload steps combined.
export async function encryptAndSendFile({ companyId, thread, userId, identity, file, onProgress }) {
  if (!companyId || !thread?.id || !userId || !identity?.privateKey || !identity?.publicKey) {
    throw new Error('Cannot send — chat is not ready');
  }
  if (!file) return null;
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('File is too large (50MB max)');
  }

  const memberIds = thread.memberIds || [];
  const fk = await generateSymmetricKey();

  const fileBuffer = await file.arrayBuffer();
  const plainBytes = new Uint8Array(fileBuffer);
  const encryptedBytes = await encryptFileBytes(plainBytes, fk, (frac) => {
    if (onProgress) onProgress(frac * 0.5); // encryption = first half of progress
  });

  const { ciphertext: nameCiphertext, nonce: nameNonce } = await secretboxEncrypt(file.name || 'attachment', fk);

  const messageId = newChatMessageId(companyId, thread.id);
  const storagePath = `companies/${companyId}/chatFiles/${thread.id}/${messageId}`;
  const storageRef = ref(storage, storagePath);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, encryptedBytes);
    task.on(
      'state_changed',
      (snap) => { if (onProgress) onProgress(0.5 + (snap.bytesTransferred / snap.totalBytes) * 0.5); },
      reject,
      resolve
    );
  });

  const wrappedKeys = {};
  await Promise.all(memberIds.map(async (memberId) => {
    const recipientPublicKey = await getMemberPublicKey(companyId, memberId);
    if (!recipientPublicKey) return; // hasn't set up chat keys yet — nothing to wrap to
    const { wrappedKey, nonce: nonce2 } = await boxWrapKey(fk, recipientPublicKey, identity.privateKey);
    wrappedKeys[memberId] = { wrappedKey, nonce2 };
  }));

  if (Object.keys(wrappedKeys).length === 0) {
    // No one can read this — clean up the orphaned ciphertext blob rather
    // than leaving a dangling Storage object with no corresponding message.
    try { await deleteObject(storageRef); } catch { /* best-effort cleanup */ }
    throw new Error('No recipients have set up chat encryption yet — file not sent');
  }

  return setChatMessage(companyId, thread.id, messageId, {
    senderId: userId,
    senderPublicKey: identity.publicKey,
    type: 'file',
    wrappedKeys,
    fileMeta: {
      encryptedName: { ciphertext: nameCiphertext, nonce: nameNonce },
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      storagePath,
    },
  });
}

// Unwraps `fk` and decrypts just the filename for a 'file' message, without
// downloading the (potentially large) file bytes — used to render the
// attachment row (name + size) before the user chooses to download it.
// Throws under the same conditions as decryptMessage in encryptedMessaging.js
// (no key for you, wrong keypair, tampered data).
export async function decryptFileMeta(msg, identity, userId) {
  if (!identity?.privateKey) throw new Error('Chat is locked');
  const myEntry = msg.wrappedKeys?.[userId];
  if (!myEntry) throw new Error('No key for you on this message');
  const fk = await boxUnwrapKey(myEntry.wrappedKey, myEntry.nonce2, msg.senderPublicKey, identity.privateKey);
  const { encryptedName, mimeType, size } = msg.fileMeta || {};
  const name = encryptedName
    ? await secretboxDecrypt(encryptedName.ciphertext, encryptedName.nonce, fk)
    : 'attachment';
  return { name, mimeType: mimeType || 'application/octet-stream', size: size || 0, fk };
}

// Downloads the encrypted bytes from Storage and decrypts them with `fk`
// (re-derived via decryptFileMeta rather than passed in, so callers never
// have to juggle the raw key themselves). Returns a Blob ready to preview or
// save, plus the decrypted name/mimeType for the browser download prompt.
export async function downloadAndDecryptFile(msg, identity, userId, onProgress) {
  const { name, mimeType, fk } = await decryptFileMeta(msg, identity, userId);
  const storagePath = msg.fileMeta?.storagePath;
  if (!storagePath) throw new Error('Missing attachment storage path');
  if (onProgress) onProgress(0.1);
  const buffer = await getBytes(ref(storage, storagePath));
  if (onProgress) onProgress(0.6);
  const plainBytes = await decryptFileBytes(new Uint8Array(buffer), fk);
  if (onProgress) onProgress(1);
  const blob = new Blob([plainBytes], { type: mimeType });
  return { blob, name, mimeType };
}
